import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { IoChevronDown } from "react-icons/io5";
import MainLayout from "@/Component/MainLayout";
import CopyCouponButton from "@/Component/Common/CopyCouponButton";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import DownloadNH from "@/Component/Common/DownloadNH";
import PageHero from "@/Component/Common/PageHero";
import {
  fetchCouponCategoryList,
  fetchCouponList,
  fetchCouponModuleList,
} from "@/services/api/common";
import {
  COUPON_TYPE_OPTIONS,
  DEFAULT_COUPON_FILTERS,
  filterCoupons,
  formatCouponExpiry,
  getCouponDetailPath,
  getCouponHeroImage,
  getPrimaryCategoryLabel,
} from "@/utils/couponHelpers";
import { withRegionPath } from "@/utils/session";
import useIsDesktop from "@/hooks/useIsDesktop";
import styles from "./Coupons.module.css";
import SeeTheWorld from "@/Component/Common/SeeTheWorld";

function toggleFilterValue(list = [], value) {
  const id = String(value);
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

function CouponCard({ coupon }) {
  const categoryLabel =
    getPrimaryCategoryLabel(coupon.categoryNames) || coupon.couponTag || "Offer";

  const detailHref = coupon.couponSlug
    ? withRegionPath(getCouponDetailPath(coupon.couponSlug))
    : "";

  const cardContent = (
    <article className={styles.card}>
      <span className={styles.categoryBadge}>{categoryLabel}</span>

      <h3 className={styles.cardTitle}>{coupon.name}</h3>
      {coupon.description ? <p className={styles.cardDesc}>{coupon.description}</p> : null}

      <div className={styles.cardFooter}>
        <div className={styles.validTill}>
          <p className={styles.couponDate2}>Valid till</p>
          <p className={styles.couponDate}>{formatCouponExpiry(coupon.expiryDate)}</p>
        </div>

        <div className={styles.codeBox}>
          <span className={styles.codeText}>{coupon.code}</span>
          <CopyCouponButton code={coupon.code} className={styles.copyBtn} iconSize={13} />
        </div>
      </div>
    </article>
  );

  if (!detailHref) return cardContent;

  return (
    <Link href={detailHref} className={styles.cardLink}>
      {cardContent}
    </Link>
  );
}

function getFilterOptionLabel(option, groupTitle = "") {
  const label = option.name || option.label || "";
  if (groupTitle === "Module" && label === "Hotel") return "Stays";
  return label;
}

function FilterCheckboxGroup({ title, options, selectedIds, onToggle }) {
  if (!options.length) return null;

  return (
    <div className={styles.filterGroup}>
      <h3 className={styles.filterGroupTitle}>{title}</h3>
      <div className={styles.filterList}>
        {options.map((option) => {
          const id = String(option.id);
          return (
            <label key={id} className={styles.filterOption}>
              <span className={styles.filterLabel}>
                {getFilterOptionLabel(option, title)}
              </span>
              <input
                type="checkbox"
                className={styles.filterCheckbox}
                checked={selectedIds.includes(id)}
                onChange={() => onToggle(id)}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function Coupons({
  initialCouponList = null,
  initialCategories = [],
  initialModules = [],
  initialHomePageHeading = null,
}) {
  const contentRef = useRef(null);
  const { isDesktop, mounted } = useIsDesktop(900);
  const [couponList, setCouponList] = useState(initialCouponList);
  const [categories, setCategories] = useState(initialCategories);
  const [modules, setModules] = useState(initialModules);
  const [loading, setLoading] = useState(
    !initialCouponList && !initialCategories.length && !initialModules.length
  );
  const [filters, setFilters] = useState(DEFAULT_COUPON_FILTERS);
  const [mobileCategory, setMobileCategory] = useState("");
  const [mobileModule, setMobileModule] = useState("");
  const [mobileType, setMobileType] = useState("");

  const loadCouponsPageData = useCallback(async () => {
    setLoading(true);
    try {
      const [listResponse, categoryResponse, moduleResponse] = await Promise.all([
        fetchCouponList(),
        fetchCouponCategoryList(),
        fetchCouponModuleList(),
      ]);

      setCouponList(listResponse?.data || null);
      setCategories(Array.isArray(categoryResponse?.data) ? categoryResponse.data : []);
      setModules(Array.isArray(moduleResponse?.data) ? moduleResponse.data : []);
    } catch (error) {
      console.error("Failed to load coupons page data:", error);
      setCouponList(null);
      setCategories([]);
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialCouponList || initialCategories.length || initialModules.length) {
      setCouponList(initialCouponList);
      setCategories(initialCategories);
      setModules(initialModules);
      setLoading(false);
      return;
    }

    loadCouponsPageData();
  }, [
    initialCouponList,
    initialCategories,
    initialModules,
    loadCouponsPageData,
  ]);

  const effectiveFilters = useMemo(() => {
    const useMobileFilters = mounted && !isDesktop;

    return {
      categoryIds:
        useMobileFilters && mobileCategory ? [mobileCategory] : filters.categoryIds,
      moduleIds: useMobileFilters && mobileModule ? [mobileModule] : filters.moduleIds,
      types: useMobileFilters
        ? mobileType
          ? [mobileType]
          : []
        : filters.types,
    };
  }, [filters, mobileCategory, mobileModule, mobileType, isDesktop, mounted]);

  const filteredCoupons = useMemo(
    () => filterCoupons(couponList, effectiveFilters),
    [couponList, effectiveFilters]
  );

  const banner = initialHomePageHeading?.topBanner;
  const fallbackHero = getCouponHeroImage(couponList);
  const webImageUrl = useMemo(() => {
    if (!banner?.webImageUrl) return fallbackHero;
    if (mounted && !isDesktop && banner.mobileImageUrl) {
      return banner.mobileImageUrl;
    }
    return banner.webImageUrl;
  }, [banner, mounted, isDesktop, fallbackHero]);
  const heroAlt = banner?.webAltTag || "Coupons banner";

  const handleClearFilters = () => {
    setFilters(DEFAULT_COUPON_FILTERS);
    setMobileCategory("");
    setMobileModule("");
    setMobileType("");
  };

  if (loading) {
    return (
      <MainLayout hideHeader>
        <div className={styles.loadingWrap}>
          <Image
            src="/assets/loading.gif"
            alt="Loading coupons"
            width={100}
            height={100}
          />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideHeader>
      <PageHero
        webImageUrl={webImageUrl}
        alt={heroAlt}
        scrollTargetRef={contentRef}
      />
      <Breadcrumbs />
      <div className={`standard-padding ${styles.contentWrap}`}>
        <div className={styles.mobileFilters}>
          <div className={styles.mobileSelectWrap}>
            <select
              className={styles.mobileSelect}
              value={mobileCategory}
              onChange={(event) => setMobileCategory(event.target.value)}
              aria-label="Filter by category"
            >
              <option value="">Category</option>
              {categories.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.name}
                </option>
              ))}
            </select>
            <IoChevronDown className={styles.mobileSelectChevron} aria-hidden="true" />
          </div>

          <div className={styles.mobileSelectWrap}>
            <select
              className={styles.mobileSelect}
              value={mobileModule}
              onChange={(event) => setMobileModule(event.target.value)}
              aria-label="Filter by module"
            >
              <option value="">Module</option>
              {modules.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.name}
                </option>
              ))}
            </select>
            <IoChevronDown className={styles.mobileSelectChevron} aria-hidden="true" />
          </div>

          <div className={styles.mobileSelectWrap}>
            <select
              className={styles.mobileSelect}
              value={mobileType}
              onChange={(event) => setMobileType(event.target.value)}
              aria-label="Filter by type"
            >
              <option value="">Type</option>
              {COUPON_TYPE_OPTIONS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            <IoChevronDown className={styles.mobileSelectChevron} aria-hidden="true" />
          </div>
        </div>

        <div className={styles.layout}>
          <aside className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <h2 className={styles.sidebarTitle}>Filters</h2>
              <button type="button" className={styles.clearBtn} onClick={handleClearFilters}>
                Clear
              </button>
            </div>

            <FilterCheckboxGroup className={styles.sidebarHeader2}
              title="Category"
              options={categories}
              selectedIds={filters.categoryIds}
              onToggle={(id) =>
                setFilters((prev) => ({
                  ...prev,
                  categoryIds: toggleFilterValue(prev.categoryIds, id),
                }))
              }
            />

            <FilterCheckboxGroup className={styles.sidebarHeader2}
              title="Module"
              options={modules}
              selectedIds={filters.moduleIds}
              onToggle={(id) =>
                setFilters((prev) => ({
                  ...prev,
                  moduleIds: toggleFilterValue(prev.moduleIds, id),
                }))
              }
            />

            <FilterCheckboxGroup className={styles.sidebarHeader2}
              title="Type"
              options={COUPON_TYPE_OPTIONS}
              selectedIds={filters.types}
              onToggle={(id) =>
                setFilters((prev) => ({
                  ...prev,
                  types: toggleFilterValue(prev.types, id),
                }))
              }
            />
          </aside>

          <div className={styles.grid}>
            {filteredCoupons.length ? (
              filteredCoupons.map((coupon) => (
                <CouponCard key={coupon.id} coupon={coupon} />
              ))
            ) : (
              <p className={`nodata ${styles.emptyState}`}>No coupons available</p>
            )}
          </div>
        </div>
      </div>

      <DownloadNH />
      <SeeTheWorld
        title={initialHomePageHeading?.heading?.title}
        text={initialHomePageHeading?.heading?.description}
      />
    </MainLayout>
  );
}
